/** THE LAYERS PANEL (sheet row 97): one row per view layer, in the sheet's
 *  order, each with its on/off button and its opacity slider. The walls have
 *  no off button: they can be faded, never hidden (rows 94–95).
 *
 *  A layer below 100% is for reading, not editing, so its row shows a padlock,
 *  as CAD layer lists do, and the padlock is the lever that makes it editable
 *  again: a field that
 *  only went quiet would leave the architect guessing why a click on a faded
 *  bed selects the room under it. */
import { useT } from "../../../i18n";
import type { Cheie } from "../../../i18n";
import { Icoana } from "../icoane";
import { editable, LAYER_IDS, minimulLayerului, type Layer, type LayerId, type Layere } from "./layers";

export default function LayersPanel({ layere, laSchimbare }: {
  layere: Layere;
  laSchimbare: (id: LayerId, patch: Partial<Layer>) => void;
}) {
  const t = useT();
  return (
    <aside className="ob-ed-layere" aria-label={t("editor.layere.titlu")}>
      <div className="ob-ed-layere-cap">
        <span className="ob-ed-titlu">{t("editor.layere.titlu")}</span>
        <button type="button" className="ob-ed-layere-tot"
                onClick={() => {
                  for (const id of LAYER_IDS) laSchimbare(id, { on: true, opacity: 1 });
                }}>
          {t("editor.layere.arata_tot")}
        </button>
      </div>
      {LAYER_IDS.map((id) => {
        const l = layere[id];
        const nume = t(`editor.layere.${id}` as Cheie);
        const procente = Math.round(l.opacity * 100);
        return (
          <div key={id} className={`ob-ed-layer${l.on ? "" : " stins"}`} data-layer-row={id}>
            <div className="ob-ed-layer-sus">
              {id === "walls" ? <span className="ob-ed-layer-loc" /> : (
                <button type="button" className={`ob-ed-layer-comuta${l.on ? " on" : ""}`}
                        aria-pressed={l.on}
                        title={t(l.on ? "editor.layere.stinge" : "editor.layere.aprinde",
                                 { strat: nume })}
                        onClick={() => laSchimbare(id, { on: !l.on })}>
                  <span aria-hidden="true" />
                </button>
              )}
              <span className="ob-ed-layer-nume">{nume}</span>
              {!editable(l) && (
                <button type="button" className="ob-ed-layer-citire"
                        aria-label={t("editor.layere.doar_citire")}
                        title={t("editor.layere.doar_citire_titlu")}
                        onClick={() => laSchimbare(id, { on: true, opacity: 1 })}>
                  <Icoana nume="lacat" marime={14} />
                </button>
              )}
            </div>
            <div className="ob-ed-layer-jos">
              <input type="range" min={Math.round(minimulLayerului(id) * 100)} max={100}
                     step={10} value={procente}
                     aria-label={t("editor.layere.opacitate", { strat: nume })}
                     onChange={(e) => laSchimbare(id, { opacity: Number(e.target.value) / 100 })}
                     // The canvas ignores keys aimed at an input, so a slider let go with
                     // the mouse hands the keyboard back; one reached with Tab keeps it.
                     onPointerUp={(e) => e.currentTarget.blur()} />
              <span className="ob-ed-layer-val">{procente}%</span>
            </div>
          </div>
        );
      })}
    </aside>
  );
}
